import React from 'react';
import { Container, Typography, Box, Grid, Button } from '@mui/material';
import { LinkedIn } from '@mui/icons-material';
import PageTransition from '../components/animations/PageTransition';

const About: React.FC = () => {
    const portfolioHighlights = [
        'Domain-specific, low-latency speech agents built for real-world workflows.',
        'AI workflows that support longitudinal analysis across complex data and user histories.',
        'Neurosymbolic AI systems that combine model-driven reasoning with structured knowledge.',
        'Retrieval architectures for RAG, including domain-specific indexing, grounding, and evaluation.',
        'Co-founded a blockchain-based crowdfunding platform for independent media creators.'
    ];

    return (
        <PageTransition>
            <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
                <Typography variant="h2" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
                    About Me
                </Typography>

                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: { xs: 5, md: 7 }
                }}>
                    <Box
                        component="img"
                        src="/jcfArmsCrossed500x500.jpg"
                        alt="John Fantell"
                        sx={{
                            width: { xs: 200, sm: 250, md: 300 },
                            height: { xs: 200, sm: 250, md: 300 },
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '4px solid',
                            borderColor: 'primary.main',
                            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: '0 12px 40px rgba(59, 130, 246, 0.4)',
                            }
                        }}
                    />
                </Box>
                
                <Grid container spacing={{ xs: 5, md: 7 }} alignItems="flex-start">
                    <Grid item xs={12} md={7}>
                        <Typography
                            variant="h3"
                            sx={{
                                mb: 3,
                                color: '#f8fafc',
                                fontSize: { xs: '2rem', md: '2.6rem' },
                                lineHeight: 1.15
                            }}
                        >
                            I build AI systems that actually do useful things.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                            I’ve worked across product, engineering, infrastructure, and applied AI, leading teams and building systems from early ideas through production. I like the messy middle: turning ambiguous problems into software that works, scales, and is useful to real people.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                            Right now I’m focused on advanced AI agents, persistent memory, structured knowledge systems, and real-time speech/audio interfaces, including full-duplex conversation. I’m most interested in applied AI: experimenting quickly, learning from real usage, and staying close to the latest research and tooling without getting stuck in theory.
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                            Outside of work, I’m into traveling, skiing, and martial arts. I’ve been to 25 countries and counting, and I’m usually thinking about the next place to explore.
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={5}>
                        <Typography variant="h4" gutterBottom color="primary" sx={{ mb: 3 }}>
                            Things I’ve Built
                        </Typography>
                        {portfolioHighlights.map((highlight) => (
                            <Box
                                key={highlight}
                                sx={{
                                    mb: 2.5,
                                    pl: 2.5,
                                    borderLeft: '3px solid',
                                    borderColor: 'primary.main'
                                }}
                            >
                                <Typography variant="body1" sx={{ fontSize: '1.03rem', lineHeight: 1.65 }}>
                                    {highlight}
                                </Typography>
                            </Box>
                        ))}

                        <Box sx={{ mt: 4 }}>
                            <Button
                                variant="contained"
                                startIcon={<LinkedIn />}
                                href="https://linkedin.com/in/johnfantell"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    background: 'linear-gradient(45deg, #0077B5, #00A0DC)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #005885, #0077B5)',
                                    }
                                }}
                            >
                                See LinkedIn for Full Experience
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </PageTransition>
    );
};

export default About;
